import { createSelector } from "@reduxjs/toolkit";
import { IRoomSummary, IRoomsSummaryHashmap } from "@backend/src/schemas";
import { IRootState } from "../app/store";
import { selectGetRoomsArgs } from "../McBim/mcbimSelectors";
import { openapi } from "../../openapi";
import {
  COLOR_FILTERS,
  GRAY,
  IColorFilterItem,
  makeStandardRoomValidationColorFn,
  makeValidationDecisionColorFn,
  STANDARD_VALIDATION_COLOR_SPECS,
  VALIDATION_DECISION_COLOR_SPECS,
} from "../DesignTab/LeftSidebar/ColorPanel/colorPanelUtils";

// ----- Grundselectorer -----

export const selectProjectNo = (state: IRootState) => state.design.projectNo;
const selectedRoomNr = (state: IRootState) => state.design.selectedSpaceNumber;

const EMPTY_ROOMS_CURRENT_FLOOR: IRoomsSummaryHashmap = {};
const EMPTY_ROOMS: IRoomsSummaryHashmap = {};

// ----- Rooms via Multiplacer / RTK Query -----

/**
 * Hämtar query-resultatet (senaste i cachen) för nuvarande args (per våningsplan / aktiva McBIM-namn)
 */
export const selectHttpGetRoomsCurrentFloorResult = (state: IRootState): IRoomsSummaryHashmap => {
  const args = selectGetRoomsArgs(state);

  if (!args) return EMPTY_ROOMS_CURRENT_FLOOR;

  return (
    openapi.endpoints.postMultiplacerRooms.select(args)(state).data ?? EMPTY_ROOMS_CURRENT_FLOOR
  );
};

/**
 * Hämtar alla rooms på projektet (utan filtrering på McBIM-space)
 */
export const selectHttpGetAllRoomsOnProjectResult = createSelector(
  [selectProjectNo, (state: IRootState) => state],
  (projectNo, state): IRoomsSummaryHashmap => {
    if (!projectNo) return EMPTY_ROOMS;

    const args = {
      body: {
        projectNo,
      },
    };

    return (
      openapi.endpoints.postMultiplacerRooms.select(args)(state).data ??
      (EMPTY_ROOMS as IRoomsSummaryHashmap)
    );
  }
);

// ----- Växla datakälla beroende på aktiv flik -----

const selectActiveProjectTab = (state: IRootState) => state.global.activeProjectTab;

const selectHttpRoomsBasedOnActiveProjectTab = createSelector(
  [
    selectActiveProjectTab,
    selectHttpGetAllRoomsOnProjectResult,
    selectHttpGetRoomsCurrentFloorResult,
  ],
  (activeTab, allRooms, currentFloorRooms): IRoomsSummaryHashmap => {
    if (activeTab === "design") {
      return currentFloorRooms;
    }
    return allRooms;
  }
);

// ----- Aktiva rum -----

export const selectActiveSingleRoomSummary = createSelector(
  [selectedRoomNr, selectHttpGetRoomsCurrentFloorResult],
  (roomNr, rooms): IRoomSummary | null => {
    if (roomNr == null || rooms == null) {
      return null;
    }
    return rooms[roomNr] ?? null;
  }
);

export const selectActiveRoom = createSelector(
  [selectedRoomNr, (state: IRootState, rooms: IRoomSummary[]) => rooms],
  (roomNr, rooms): IRoomSummary | undefined => {
    if (!roomNr) return undefined;
    return rooms.find((room) => room.roomNumber === roomNr);
  }
);

// export const selectCurrentGetSingleRoomParams = (state: IRootState) => {
//   return {
//     roomNumber: state.design.selectedSpaceNumber as string,
//   };
// };

// ----- Standardrom-validering → dynamiska färgfilter -----

export const selectUniqueStandardRoomValidationsKeys = createSelector(
  [selectHttpRoomsBasedOnActiveProjectTab],
  (roomsHashmap): string[] => {
    const unique = new Set<string>();

    Object.values(roomsHashmap).forEach((room) => {
      const standardRoom = room?.validation?.standardRoom;
      if (standardRoom && typeof standardRoom === "object") {
        Object.keys(standardRoom).forEach((key) => {
          // Lägg till "standardRoom.E", "standardRoom.V" osv.
          unique.add(`standardRoom.${key}`);
        });
      }
    });

    return Array.from(unique).sort();
  }
);

const DECISION_FILTER_PREFIX = "decision:" as const;

export type IDecisionFilterId = `${typeof DECISION_FILTER_PREFIX}${string}.${string}`;

// returnerar t.ex. ["decision:standardRoom.E", "decision:standardRoom.V"]
export const selectUniqueValidationDecisionKeys = createSelector(
  [selectHttpGetRoomsCurrentFloorResult],
  (roomsMap): IDecisionFilterId[] => {
    if (!roomsMap) return [];

    const keys = new Set<IDecisionFilterId>();

    for (const room of Object.values(roomsMap)) {
      const std = room.validationDecisions?.standardRoom;
      if (!std) continue;

      for (const discipline of Object.keys(std)) {
        keys.add(`${DECISION_FILTER_PREFIX}standardRoom.${discipline}`);
      }
    }

    return Array.from(keys).sort();
  }
);

const titleForDecisionKey = (key: string) => {
  // "standardRoom.E" -> "Standardrom – E" (justera texten som du vill)
  const [branch, discipline] = key.split(".");
  if (branch === "standardRoom") return `Beslutning - Standardrom – ${discipline}`;
  return `Beslutning - ${branch} – ${discipline}`;
};

const toDecisionKey = (id: IDecisionFilterId) =>
  id.replace(DECISION_FILTER_PREFIX, "") as `${string}.${string}`;

export const selectValidationDecisionColorItems = createSelector(
  [selectUniqueValidationDecisionKeys],
  (keys): IColorFilterItem[] => {
    return keys.map((id) => {
      const decisionKey = toDecisionKey(id);

      return {
        id, // "decision:standardRoom.E"
        title: titleForDecisionKey(decisionKey),
        colorSpecs: VALIDATION_DECISION_COLOR_SPECS,
        trendKeyPath: `validationDecisions.${decisionKey}`,
        getPolygonColors: makeValidationDecisionColorFn(decisionKey),
      };
    });
  }
);

// Hjälp: norsk titel baserat på nyckeln
const titleForKey = (key: `standardRoom.${string}`): string => {
  const disiplin = key.split(".")[1] ?? "";
  return `Standardrom – validering (${disiplin})`;
};

export const selectStandardRoomValidationColorItems = createSelector(
  [selectUniqueStandardRoomValidationsKeys],
  (keys): IColorFilterItem[] => {
    return keys.map((key) => ({
      id: key, // t.ex. "standardRoom.E"
      title: titleForKey(key as `standardRoom.${string}`),
      colorSpecs: STANDARD_VALIDATION_COLOR_SPECS,
      trendKeyPath: `validation.standardRoom.${key.split(".")[1]}.summary.matchRatePct`,
      getPolygonColors: makeStandardRoomValidationColorFn(key as `standardRoom.${string}`),
    }));
  }
);

// Kombinerar statiska färgfilter med dynamiska standardrom-filter + decision-filter
export const selectGetColorFilters = createSelector(
  [
    () => COLOR_FILTERS, // stabil referens till dina statiska filter
    selectStandardRoomValidationColorItems, // dynamiskt byggda standardRoom-filter (matchRate etc)
    selectValidationDecisionColorItems, // ✅ dynamiskt byggda decision-status-filter (current.status)
  ],
  (staticFilters, standardRoomFilters, decisionFilters): IColorFilterItem[] => {
    // undvik dubletter på id; statiska prioriteras
    const existing = new Set(staticFilters.map((f) => f.id));

    const onlyNewStandard = standardRoomFilters.filter((f) => !existing.has(f.id));
    onlyNewStandard.forEach((f) => existing.add(f.id));

    const onlyNewDecisions = decisionFilters.filter((f) => !existing.has(f.id));

    return [...staticFilters, ...onlyNewStandard, ...onlyNewDecisions];
  }
);

const selectActiveColorPanelItem = (state: IRootState) => state.design.activeColorPanelItem;
export const selectActiveColorFilterItemFull = createSelector(
  [selectGetColorFilters, selectActiveColorPanelItem],
  (colorFilters, activeColorPanelItem) => {
    if (!activeColorPanelItem) return null;
    return colorFilters.find((filter) => filter.id === activeColorPanelItem) || null;
  }
);

export const selectHttpSingleRoomFull = createSelector(
  [selectProjectNo, selectedRoomNr, (state: IRootState) => state],
  (projectNo, roomNr, state) => {
    if (!projectNo || !roomNr) return null;

    const args = {
      projectNo: Number(projectNo),
      roomNumber: roomNr,
    };

    return openapi.endpoints.getMultiplacerSingleRoom.select(args)(state).data ?? null;
  }
);

// const colorFilter = useAppSelector((state) => state.design.activeColorPanelItem);

// // matchar t.ex. "standardRoom.E" → "E"
// const discipline = colorFilter?.match(/^standardRoom\.(.)/)?.[1];

export const selectColorFilter = (state: IRootState) => state.design.activeColorPanelItem;
export const selectRfpStandardRoomDisciplineFromColorFilter = createSelector(
  [selectColorFilter],
  (colorFilter): string | undefined => {
    const discipline = colorFilter?.match(/^standardRoom\.(.)/)?.[1];
    return discipline ?? undefined;
  }
);

export const selectAvailableStandardRoomValidationDisciplines = createSelector(
  [selectHttpSingleRoomFull],
  (room) => {
    const disciplines: string[] = [];
    const standardRoomValidation = room?.validation?.standardRoom;
    if (standardRoomValidation && typeof standardRoomValidation === "object") {
      Object.keys(standardRoomValidation).forEach((key) => {
        disciplines.push(key as string);
      });
    }
    return disciplines;
  }
);

export const selectStandardRoomValidationInfoInSelectedRoom = createSelector(
  [selectHttpSingleRoomFull, selectRfpStandardRoomDisciplineFromColorFilter],
  (room, disciplineTab) => {
    if (!room || !disciplineTab) return null;
    const standardRoomValidation = room.validation?.standardRoom;
    if (!standardRoomValidation) return null;
    return standardRoomValidation[disciplineTab] ?? null;
  }
);
export const selectMlDevicesAvailableInSelectedSpace = createSelector(
  [selectHttpSingleRoomFull],
  (singleRoom) => {
    if (!singleRoom) {
      return false;
    }
    const count = singleRoom.mlPredictedDevices?.length ?? 0;
    return count > 0;
  }
);

export const selectActiveRoomHasStandardRoom = createSelector(
  [selectActiveSingleRoomSummary],
  (room) => {
    if (!room) {
      return false;
    }

    return room.rfpMalRom != null && room.rfpMalRom !== "";
  }
);

export const selectActiveRoomIsLocked = createSelector([selectHttpSingleRoomFull], (room) => {
  if (!room) {
    return false;
  }
  return room.multiplacerCeilingDevicesLocked?.bool === true;
});

const getStandardRoomDisciplineFromColorFilterId = (
  activeId: string | undefined
): string | undefined => {
  if (!activeId) return undefined;

  // "decision:standardRoom.E" -> "standardRoom.E"
  const withoutPrefix = activeId.startsWith(DECISION_FILTER_PREFIX)
    ? activeId.slice(DECISION_FILTER_PREFIX.length)
    : activeId;

  // Vi bryr oss bara om standardRoom.*
  if (!withoutPrefix.startsWith("standardRoom.")) return undefined;

  const parts = withoutPrefix.split(".");
  return parts[1] ?? null; // "E"
};

export const selectStandardRoomDisciplineFromActiveColorFilter = createSelector(
  [selectActiveColorPanelItem],
  (activeId) => getStandardRoomDisciplineFromColorFilterId(activeId)
);

export const selectRoomNumbers = createSelector(
  [selectHttpRoomsBasedOnActiveProjectTab],
  (roomsMap): string[] => {
    return Object.values(roomsMap).map((room) => room.roomNumber.toString());
  }
);
